import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Feather, Check, ChevronDown, ChevronUp,
} from "lucide-react";

/**
 * The Intentional Self Profile — six deep sections of reflective self-inquiry.
 *
 * This is the redesigned experience: less questionnaire, more ceremony.
 * Every prompt is aimed at *who a member is becoming* — not what they want
 * to accomplish. Some prompts are free-form (a textarea invites their own
 * words). A few are chip-based value selections with an optional note, so
 * a member can pick language quickly *or* answer entirely in their own
 * voice. Nothing is scored, ranked, or diagnosed.
 *
 * Storage schema (backward compatible):
 *   profile.sacred_aging[<section_slug>][<prompt_key>] = string | string[] |
 *     { selected: string[], note?: string }
 *
 * Legacy answers from the previous eight-part sections remain readable
 * under their original slugs and are surfaced in a soft "Previously
 * reflected" accordion at the bottom of the page.
 */

const SECTIONS = [
  {
    slug: "becoming", // preserved slug — old answers still readable
    numeral: "I",
    title: "Who I Am",
    kicker: "Identity at the Core",
    intro: "Beneath the roles and expectations, there is a self that is already yours.",
    prompts: [
      {
        key: "authentic_version",
        kind: "text",
        label: "Who are you, at your core — beneath the roles, achievements, and expectations?",
        placeholder: "The version of me who…",
      },
      {
        key: "qualities_embody",
        kind: "chips",
        label: "Which qualities feel most true to who you are right now?",
        chips: [
          "Presence", "Courage", "Tenderness", "Discipline", "Playfulness",
          "Patience", "Honesty", "Boldness", "Softness", "Curiosity",
          "Steadiness", "Generosity", "Wildness", "Devotion", "Levity",
        ],
        noteLabel: "Anything else worth naming?",
      },
    ],
  },
  {
    slug: "matters", // NEW slug
    numeral: "II",
    title: "What Matters to Me",
    kicker: "The Substance Beneath Success",
    intro: "What you quietly protect is what you actually value.",
    prompts: [
      {
        key: "meaningful_definition",
        kind: "text",
        label: "What do you protect, quietly and consistently, because it truly matters to you?",
        placeholder: "The moments when…",
      },
      {
        key: "anchors",
        kind: "chips",
        label: "Which values anchor the way you want to live?",
        chips: [
          "Presence", "Health", "Family", "Freedom", "Creativity",
          "Integrity", "Solitude", "Connection", "Simplicity", "Craft",
          "Devotion", "Adventure", "Learning", "Beauty", "Service",
        ],
        noteLabel: "In your own words…",
      },
    ],
  },
  {
    slug: "wants", // NEW slug
    numeral: "III",
    title: "What I Want",
    kicker: "The Desires You're Claiming",
    intro: "Some desires are soft invitations. Others are quiet declarations.",
    prompts: [
      {
        key: "more_experiences",
        kind: "chips",
        label: "What experiences do you want more of in this chapter?",
        chips: [
          "Quiet mornings", "Deep conversation", "Movement outdoors",
          "Art & creation", "Travel", "Cooking together", "Real rest",
          "Laughter", "Learning something new", "Solitude", "Being held",
          "Awe", "Making things by hand", "Water", "Music",
        ],
        noteLabel: "What else are you hungry for?",
      },
      {
        key: "claiming",
        kind: "text",
        label: "What are you claiming for yourself — the desires you're no longer willing to ignore?",
        placeholder: "I want…",
      },
    ],
  },
  {
    slug: "outgrowing",
    numeral: "IV",
    title: "What I'm Outgrowing",
    kicker: "The Weight You No Longer Carry",
    intro: "Growth is as much what you set down as what you take up.",
    prompts: [
      {
        key: "carrying_outgrown",
        kind: "text",
        label: "What have you outgrown that you continue to carry?",
        placeholder: "A story about myself that…",
      },
      {
        key: "future_boundaries",
        kind: "chips",
        label: "Which patterns are you ready to set down?",
        chips: [
          "People-pleasing", "Over-explaining", "Rushing", "Overworking",
          "Self-criticism", "Scrolling", "Comparison", "Shrinking",
          "Perfectionism", "Saying yes when I mean no", "Second-guessing",
        ],
        noteLabel: "In your own words…",
      },
    ],
  },
  {
    slug: "future_self",
    numeral: "V",
    title: "My Future Self",
    kicker: "A Conversation Across Time",
    intro: "The version of you five years from now is quietly listening.",
    prompts: [
      {
        key: "start_stop",
        kind: "text",
        label: "What does your future self need you to start — and stop — doing?",
        placeholder: "Start… Stop…",
      },
      {
        key: "decision_thank_you",
        kind: "text",
        label: "What decision would your future self thank you for making today?",
        placeholder: "If I…",
      },
    ],
  },
  {
    slug: "mind_body_soul",
    numeral: "VI",
    title: "Mind • Body • Soul",
    kicker: "Three Doors, One Room",
    intro: "One reflection from each of the three pillars of your becoming.",
    prompts: [
      {
        key: "mind_question",
        kind: "text",
        label: "Mind — What belief about yourself are you ready to question?",
        placeholder: "The belief that…",
      },
      {
        key: "body_experience",
        kind: "text",
        label: "Body — How do you want to experience your body — not just look in it?",
        placeholder: "Strong. Held. At home in.",
      },
      {
        key: "soul_connection",
        kind: "text",
        label: "Soul — What makes you feel most connected to something greater than yourself?",
        placeholder: "When I…",
      },
    ],
  },
  {
    slug: "life_creating",
    numeral: "VII",
    title: "The Life I'm Creating",
    kicker: "One Long Look Forward",
    intro: "The most important reflection. Answer this one slowly — in your own voice.",
    isFinale: true,
    prompts: [
      {
        key: "aligned_life",
        kind: "text",
        rows: 8,
        label: "If your health, relationships, mindset, body, and inner life were all aligned with the person you're becoming, what would your life look and feel like?",
        placeholder: "Take your time. Describe an ordinary morning. Describe the way you speak to yourself. Describe the people around your table…",
      },
    ],
  },
];

// Any section slug we no longer render but still want to preserve when a
// prior member had already reflected there. Rendered read-only in the
// "Previously reflected" accordion so nothing is lost.
const LEGACY_SLUGS = new Set([
  "meaningful",
  "others", "self", "rituals", "longevity",
  "curiosities", "emotional", "money", "aging", "beauty",
]);

const LEGACY_LABELS = {
  meaningful: "What Makes Life Meaningful",
  others: "Your Relationship With Others",
  self: "Your Identity With Self",
  rituals: "Your Ritual Tendencies",
  longevity: "Your Longevity Desires",
  curiosities: "Your Esthetic Curiosities",
  emotional: "Your Emotional Needs",
  money: "Your Relationship With Money",
  aging: "Aging",
  beauty: "Beauty",
};

// Return the number of "answered" prompts for a section, treating chips /
// structured values as answered when a selection or note exists.
function countAnswered(section, sectionAnswers = {}) {
  let n = 0;
  for (const p of section.prompts) {
    const v = sectionAnswers[p.key];
    if (isAnswered(v)) n += 1;
  }
  return n;
}

function isAnswered(v) {
  if (v == null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") {
    const sel = Array.isArray(v.selected) ? v.selected : [];
    const note = typeof v.note === "string" ? v.note.trim() : "";
    return sel.length > 0 || note.length > 0;
  }
  return false;
}

// Render an arbitrary stored value as a short human-readable string, for
// legacy display and journal seeding.
function valueToText(v) {
  if (!v) return "";
  if (typeof v === "string") return v.trim();
  if (Array.isArray(v)) return v.filter(Boolean).join(", ");
  if (typeof v === "object") {
    const sel = Array.isArray(v.selected) ? v.selected.filter(Boolean).join(", ") : "";
    const note = typeof v.note === "string" ? v.note.trim() : "";
    return [sel, note].filter(Boolean).join(" — ");
  }
  return String(v);
}

export default function SacredAgingProfile({ profile, onBack, onSaved }) {
  const initial = useMemo(() => profile?.sacred_aging || {}, [profile]);
  const [answers, setAnswers] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [seeded, setSeeded] = useState({}); // { "<section>-<key>": true }
  const [legacyOpen, setLegacyOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { setAnswers(initial); setDirty(false); }, [initial]);

  const setAnswer = (sectionSlug, key, value) => {
    setAnswers((prev) => ({
      ...prev,
      [sectionSlug]: { ...(prev[sectionSlug] || {}), [key]: value },
    }));
    setSeeded((prev) => {
      const k = `${sectionSlug}-${key}`;
      if (!prev[k]) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
    setDirty(true);
  };

  const seedToJournal = async (section, prompt) => {
    const content = valueToText(answers?.[section.slug]?.[prompt.key]).trim();
    if (!content) {
      toast("Write a reflection first.");
      return;
    }
    const seedKey = `${section.slug}-${prompt.key}`;
    if (seeded[seedKey]) return;
    try {
      await endpoints.createJournal({
        prompt: `Intentional Self · ${section.title} · ${prompt.label}`,
        content,
        mood_tag: "reflect",
      });
      setSeeded((prev) => ({ ...prev, [seedKey]: true }));
      toast.success("Seeded to Journal.", {
        action: {
          label: "Continue writing",
          onClick: () => navigate("/journal"),
        },
      });
    } catch {
      toast.error("Could not seed just yet. Try again in a moment.");
    }
  };

  const totalCount = SECTIONS.reduce((n, s) => n + s.prompts.length, 0);
  const answeredCount = useMemo(() => {
    let n = 0;
    for (const s of SECTIONS) n += countAnswered(s, answers?.[s.slug] || {});
    return n;
  }, [answers]);

  const legacyEntries = useMemo(() => {
    const out = [];
    // Build a map of active section slug → set of currently rendered prompt keys
    const activePromptKeys = new Map();
    for (const s of SECTIONS) {
      activePromptKeys.set(s.slug, new Set(s.prompts.map((p) => p.key)));
    }
    for (const [slug, sectionAnswers] of Object.entries(answers || {})) {
      if (!sectionAnswers || typeof sectionAnswers !== "object") continue;
      // 1. Legacy sections that are no longer rendered at all
      if (LEGACY_SLUGS.has(slug)) {
        const nonEmpty = Object.entries(sectionAnswers).filter(([, v]) => isAnswered(v));
        if (nonEmpty.length) out.push({ slug, label: LEGACY_LABELS[slug] || slug, entries: nonEmpty });
        continue;
      }
      // 2. Orphan keys inside an active section (prompt was removed in this refresh)
      const activeKeys = activePromptKeys.get(slug);
      if (!activeKeys) continue;
      const section = SECTIONS.find((s) => s.slug === slug);
      const orphans = Object.entries(sectionAnswers).filter(
        ([k, v]) => !activeKeys.has(k) && isAnswered(v),
      );
      if (orphans.length && section) {
        out.push({ slug: `${slug}-orphan`, label: `${section.title} · earlier reflections`, entries: orphans });
      }
    }
    return out;
  }, [answers]);

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await endpoints.updateProfile({ sacred_aging: answers });
      toast.success("Held with care.");
      setDirty(false);
      onSaved?.();
    } catch {
      toast.error("Couldn't save just yet — try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="sacred-aging-profile">
      <button
        type="button"
        onClick={onBack}
        data-testid="sacred-back"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--clay))] tab-link"
      >
        <ArrowLeft size={12} /> Back to Profile
      </button>

      <header className="reveal">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
          <Sparkles size={12} /> The Art Of Becoming
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-3 italic">
          Your Intentional Self
        </h1>
        <div
          className="mt-6 p-6 rounded-md border-l-2 border-[hsl(var(--terracotta))] bg-gradient-to-br from-[hsl(var(--sand))]/60 to-[hsl(var(--paper))] max-w-3xl"
          data-testid="sacred-intro"
        >
          <p className="font-display text-lg italic leading-relaxed text-[hsl(var(--fg))]">
            This isn't asking what you want to accomplish. It's helping you remember who you are, decide
            who you're becoming, and intentionally build a life that reflects it.
          </p>
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
            Seven sections. Answer only what wants to be answered. Return any evening — your reflections
            deepen the way N8 shows up for you across rituals, whispers, and the way your future self is
            invited into the conversation.
          </p>
        </div>
        <div
          className="mt-4 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]"
          data-testid="sacred-progress"
        >
          {answeredCount} of {totalCount} reflections offered
        </div>
      </header>

      <div className="space-y-8">
        {SECTIONS.map((section, idx) => (
          <SacredSection
            key={section.slug}
            section={section}
            index={idx}
            answers={answers?.[section.slug] || {}}
            seeded={seeded}
            onChange={(k, v) => setAnswer(section.slug, k, v)}
            onSeed={(prompt) => seedToJournal(section, prompt)}
          />
        ))}
      </div>

      {legacyEntries.length > 0 && (
        <section
          className="p-6 border hairline rounded-md bg-[hsl(var(--sand))]/25"
          data-testid="sacred-legacy"
        >
          <button
            type="button"
            onClick={() => setLegacyOpen((v) => !v)}
            data-testid="sacred-legacy-toggle"
            className="w-full flex items-center justify-between gap-3 text-left"
          >
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
                Previously reflected
              </div>
              <div className="font-display text-lg italic mt-1">
                Reflections from an earlier version of this experience.
              </div>
              <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                Kept here so nothing you wrote is lost. Read-only.
              </p>
            </div>
            {legacyOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {legacyOpen && (
            <div className="mt-5 space-y-6" data-testid="sacred-legacy-body">
              {legacyEntries.map(({ slug, label, entries }) => (
                <div key={slug} data-testid={`sacred-legacy-${slug}`}>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
                    {label}
                  </div>
                  <ul className="mt-2 space-y-2">
                    {entries.map(([k, v]) => (
                      <li key={k} className="text-sm text-[hsl(var(--fg))]/85 leading-relaxed">
                        <span className="text-[hsl(var(--muted-foreground))] mr-2">·</span>
                        {valueToText(v)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <div
        className="sticky bottom-4 z-10 flex items-center justify-between gap-4 flex-wrap p-4 rounded-full border hairline bg-[hsl(var(--paper))]/95 backdrop-blur shadow-sm"
        data-testid="sacred-save-bar"
      >
        <div className="text-xs text-[hsl(var(--muted-foreground))] pl-3">
          {dirty ? "Unsaved reflections." : "Everything is held."}
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={onBack}
            variant="outline"
            data-testid="sacred-close"
            className="rounded-full"
          >
            Close
          </Button>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="sacred-save"
            className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
          >
            {saving ? "Holding…" : "Hold these answers"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SacredSection({ section, index, answers, seeded, onChange, onSeed }) {
  const answered = countAnswered(section, answers);
  const isFinale = !!section.isFinale;
  return (
    <section
      className={`p-8 border hairline rounded-md reveal ${
        isFinale
          ? "bg-gradient-to-br from-[hsl(var(--terracotta))]/8 via-[hsl(var(--sand))]/40 to-[hsl(var(--paper))]"
          : "bg-[hsl(var(--paper))]"
      }`}
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`sacred-section-${section.slug}`}
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="max-w-xl">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))]">
            {section.numeral} · {section.kicker}
          </div>
          <h2 className="font-display text-2xl sm:text-3xl mt-1 tracking-tight italic">{section.title}</h2>
          {section.intro && (
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] italic leading-relaxed">
              {section.intro}
            </p>
          )}
        </div>
        <div
          className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]"
          data-testid={`sacred-section-${section.slug}-count`}
        >
          {answered} / {section.prompts.length} offered
        </div>
      </div>

      <div className="mt-6 space-y-6">
        {section.prompts.map((p) => (
          <PromptRow
            key={p.key}
            sectionSlug={section.slug}
            prompt={p}
            value={answers[p.key]}
            seeded={!!(seeded && seeded[`${section.slug}-${p.key}`])}
            onChange={(v) => onChange(p.key, v)}
            onSeed={() => onSeed(p)}
          />
        ))}
      </div>
    </section>
  );
}

function PromptRow({ sectionSlug, prompt, value, seeded, onChange, onSeed }) {
  const hasContent = isAnswered(value);
  return (
    <div className="block" data-testid={`sacred-prompt-${sectionSlug}-${prompt.key}`}>
      <div className="flex items-start justify-between gap-3">
        <label
          htmlFor={`sacred-${sectionSlug}-${prompt.key}`}
          className="block text-sm text-[hsl(var(--fg))] leading-relaxed flex-1"
        >
          {prompt.label}
        </label>
        <button
          type="button"
          onClick={onSeed}
          disabled={!hasContent || seeded}
          aria-label={seeded ? "Seeded to Journal" : "Seed to Journal"}
          data-testid={`sacred-seed-${sectionSlug}-${prompt.key}`}
          className={`shrink-0 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border hairline tab-link ${
            seeded
              ? "border-[hsl(var(--moss))]/50 bg-[hsl(var(--moss))]/10 text-[hsl(var(--moss))] cursor-default"
              : hasContent
              ? "border-transparent bg-[hsl(var(--sand))]/60 text-[hsl(var(--clay))] hover:bg-[hsl(var(--sand))]"
              : "border-transparent text-[hsl(var(--muted-foreground))]/60 cursor-not-allowed"
          }`}
        >
          {seeded ? <Check size={11} /> : <Feather size={11} />}
          {seeded ? "Seeded" : "Seed to Journal"}
        </button>
      </div>

      {prompt.kind === "chips" ? (
        <ChipsField
          sectionSlug={sectionSlug}
          prompt={prompt}
          value={value}
          onChange={onChange}
        />
      ) : (
        <Textarea
          id={`sacred-${sectionSlug}-${prompt.key}`}
          value={typeof value === "string" ? value : valueToText(value)}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`sacred-${sectionSlug}-${prompt.key}`}
          rows={prompt.rows || 3}
          placeholder={prompt.placeholder || "Answer only what wants to be answered…"}
          className="mt-2 bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--clay))]"
        />
      )}
    </div>
  );
}

function ChipsField({ sectionSlug, prompt, value, onChange }) {
  // Normalize any incoming shape into { selected: [...], note: "" } so the
  // chip UI can operate on a stable structure.
  const normalized = useMemo(() => {
    if (!value) return { selected: [], note: "" };
    if (typeof value === "string") return { selected: [], note: value };
    if (Array.isArray(value)) return { selected: value, note: "" };
    return {
      selected: Array.isArray(value.selected) ? value.selected : [],
      note: typeof value.note === "string" ? value.note : "",
    };
  }, [value]);

  const toggle = (chip) => {
    const has = normalized.selected.includes(chip);
    const next = has
      ? normalized.selected.filter((c) => c !== chip)
      : [...normalized.selected, chip];
    onChange({ selected: next, note: normalized.note });
  };

  const setNote = (note) => {
    onChange({ selected: normalized.selected, note });
  };

  return (
    <div className="mt-3 space-y-3">
      <div
        className="flex flex-wrap gap-2"
        data-testid={`sacred-chips-${sectionSlug}-${prompt.key}`}
        role="group"
        aria-label={prompt.label}
      >
        {(prompt.chips || []).map((chip) => {
          const on = normalized.selected.includes(chip);
          return (
            <button
              key={chip}
              type="button"
              onClick={() => toggle(chip)}
              aria-pressed={on}
              data-testid={`sacred-chip-${sectionSlug}-${prompt.key}-${chip.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-3 py-1.5 rounded-full text-xs border hairline tab-link ${
                on
                  ? "bg-[hsl(var(--terracotta))] text-[hsl(var(--paper))] border-transparent"
                  : "text-[hsl(var(--fg))]/75 hover:bg-[hsl(var(--sand))]"
              }`}
            >
              {chip}
            </button>
          );
        })}
      </div>
      <Textarea
        id={`sacred-${sectionSlug}-${prompt.key}`}
        value={normalized.note}
        onChange={(e) => setNote(e.target.value)}
        data-testid={`sacred-${sectionSlug}-${prompt.key}-note`}
        rows={2}
        placeholder={prompt.noteLabel || "In your own words (optional)…"}
        className="bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--clay))]"
      />
    </div>
  );
}

export { SECTIONS as SACRED_AGING_SECTIONS };
